import React from 'react'
import {
  formatSnapshotDate,
  oldestSnapshotDate,
  useChildControlExports,
} from './useChildControlExports'

/**
 * Tells the user that child accounts are in the file, and how current they are.
 *
 * Not decoration. Child rows are rebuilt nightly, so they trail the app by up to a day — someone
 * reconciling an export against the Controls page will find the difference, and should not have to
 * guess at the cause. The date shown is the OLDEST across the children, because a download is as
 * stale as the most out-of-date thing in it.
 *
 * Advisory, and deliberately so. The count comes from the page's cached fetch while the file itself
 * is built from a fresh one taken when the download is confirmed, so on a long-lived tab this can
 * name a child the file then leaves out. A label that lags is not the same problem as a file that
 * does, and making the file authoritative was the point.
 *
 * Subscribes itself rather than taking the snapshots as a prop. The confirmation modal stores this
 * as a JSX node when it opens, so a prop would be frozen at that instant — click download before the
 * request lands and the notice would never appear at all, leaving a wider file with nothing saying
 * why. Owning the subscription lets it fill in when the answer arrives.
 *
 * Renders nothing when there are no children, which is almost every account.
 */
export const ChildEntitiesNote = (): JSX.Element => {
  const { snapshots } = useChildControlExports()
  if (!snapshots.length) return <></>

  const asOf = oldestSnapshotDate(snapshots)
  return (
    <p>
      Controls from <strong>{snapshots.length}</strong> child{' '}
      {snapshots.length === 1 ? 'account' : 'accounts'} are included and named
      in the Entity column
      {asOf ? <>, current as of {formatSnapshotDate(asOf)}</> : null}.
    </p>
  )
}
